import { describeApiError, type FantasyContest, type SalaryPoolPreview } from '@brush/api-client';
import {
  BuilderSection,
  CheckboxRow,
  ErrorNotice,
  IssueList,
  Labelled,
  NumberInput,
  Pill,
  SegmentedControl,
  Spinner,
  Worked,
  controlClass,
  inputClass,
  readScope,
  toScopeDocument,
  useDebouncedValue,
  type PointsScope,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  CONTEST_STATUS_DETAIL,
  CONTEST_STATUS_LABEL,
  CONTEST_STATUS_TONE,
  CONTEST_TRANSITIONS,
  ROSTER_FORMATS,
  budgetUnit,
  carryModelFacts,
  certainlyStartedEvents,
  contestDraftKey,
  contestDraftOf,
  contestIssuesFor,
  contestProblems,
  contestStatusOf,
  formatNeedsSalaries,
  fromLocalInput,
  isEditable,
  mergeDerived,
  AMENDED_AFTER_LOCK_NOTICE,
  pausedNotice,
  poolKey,
  poolProblems,
  poolRequestOf,
  publishBlockers,
  rowsFromPool,
  salaryModelMismatch,
  toLocalInput,
  type ContestDraft,
  type ContestIssue,
  type PoolRow,
  type RosterFormat,
} from '../../fantasy/contest';
import {
  useCloseFantasyContest,
  useDeleteFantasyContest,
  useDeriveFantasyPool,
  useFantasyPool,
  useLeaderboards,
  useLockFantasyContest,
  usePatchFantasyContest,
  usePublishFantasyContest,
  useSalaryModels,
  useSaveFantasyPool,
  useScopeEvents,
} from '../../queries';
import { useRoom } from '../../room';
import { useUnsavedWork } from '../../unsaved';
import { ContestDriftPanel } from './ContestDriftPanel';
import { ContestEntriesPanel } from './ContestEntriesPanel';
import { ContestLockSection } from './ContestLockSection';
import { ContestPoolReview } from './ContestPoolReview';
import { ContestReviewSection } from './ContestReviewSection';
import { ContestSalarySection } from './ContestSalarySection';
import { ContestTransitionDialog } from './ContestTransitionDialog';

/**
 * §9.1's contest builder: six sections in the order the spec fixes, and §6's lifecycle across
 * the top.
 *
 * <b>One screen for all four states, not a builder and a separate detail view.</b> The sections do
 * not stop being worth reading once a contest publishes — "what did we say the budget was" is
 * asked most often after the fact — so they stay, read-only, with the reason each control has gone
 * stated where the control was. That is §9.9's rule for a pinned points system applied to a whole
 * screen: before the transition there is a choice, afterwards there is a fact, and a greyed-out box
 * says "not now" where the truth is "never".
 *
 * <b>Two drafts, saved separately.</b> The contest's own fields go through `PATCH`; the pool goes
 * through `PUT /pool`. They are different documents with different refusals — a bad price is not a
 * bad budget — and folding them into one Save would make a rejected pool look like a rejected
 * contest. Both are guarded by `useUnsavedWork`, so every way out of the screen asks.
 *
 * <b>The scope resolution is asked for once and read three times</b> — section 1's event count,
 * section 5's first-event date and §6.1 refusal, and section 6's blockers. It shares the points
 * preview's per-caller rate limit, so three copies of that query would be three times the
 * allowance for one answer.
 */
export function ContestBuilderScreen({
  contest,
  isAdmin,
  onBack,
  onDeleted,
}: {
  contest: FantasyContest;
  /**
   * Whether the signed-in caller is a platform administrator. §6.2's drift report and its Adopt are
   * both admin-only, so an operator's screen must not fire that request — a permanent 403 notice
   * reads as a broken app rather than as a report they do not have.
   */
  isAdmin: boolean;
  /** Routed through the unsaved-work guard by the caller, like every other way out of an editor. */
  onBack: () => void;
  /**
   * Leaving after the contest has been deleted. Separate from `onBack` deliberately: it must *not*
   * go through the guard, since asking "leave without saving?" about something the operator has
   * just deliberately destroyed is nonsense — and the answer "stay" would leave them on a screen
   * for a record that no longer exists.
   */
  onDeleted: () => void;
}) {
  const { room, roomId } = useRoom();
  const status = contestStatusOf(contest);
  const editable = isEditable(contest);

  const leaderboardsQ = useLeaderboards(roomId);
  // Archived models included, and the picker filters them out itself. A published contest can
  // legitimately have pinned a version of a model that was archived afterwards, and resolving its
  // name is exactly what section 3 needs to do — excluding them here would leave that contest
  // reporting a model this room "no longer lists" when it plainly does.
  const modelsQ = useSalaryModels(roomId, true);
  const poolQ = useFantasyPool(contest.id);

  const patch = usePatchFantasyContest(roomId);
  const savePool = useSaveFantasyPool(roomId);
  const derive = useDeriveFantasyPool();
  const publish = usePublishFantasyContest(roomId);
  const lock = useLockFantasyContest(roomId);
  const close = useCloseFantasyContest(roomId);
  const remove = useDeleteFantasyContest(roomId);

  // ---------------------------------------------------------------- the contest's own fields

  const [draft, setDraft] = useState<ContestDraft>(() => contestDraftOf(contest));
  const storedDraftKey = contestDraftKey(contestDraftOf(contest));

  // Re-seeded when the *stored* contest changes — a transition, or somebody else's edit — and not
  // on every refetch, which would discard whatever is being typed. Keyed on the stored value rather
  // than on the object, since TanStack hands back a new reference each time.
  const [seededDraft, setSeededDraft] = useState(storedDraftKey);
  useEffect(() => {
    if (seededDraft !== storedDraftKey) {
      setDraft(contestDraftOf(contest));
      setSeededDraft(storedDraftKey);
    }
  }, [contest, storedDraftKey, seededDraft]);

  const contestDirty = editable && contestDraftKey(draft) !== storedDraftKey;
  const contestIssues = useMemo(() => contestProblems(draft), [draft]);

  // ---------------------------------------------------------------- the pool

  const storedRows = useMemo(
    () => (poolQ.data ? rowsFromPool(poolQ.data.players) : null),
    [poolQ.data],
  );
  const storedPoolKey = storedRows ? poolKey(storedRows) : null;

  const [rows, setRows] = useState<PoolRow[]>([]);
  const [seededPool, setSeededPool] = useState<string | null>(null);
  const [lastDerive, setLastDerive] = useState<SalaryPoolPreview | null>(null);
  // Which version produced what is on screen. `POST /pool/derive` records per-row provenance and
  // not which model it ran, so this is known only for as long as this screen has been open — see
  // `salaryModelMismatch`, whose other case is checked against the rows and survives a reload.
  const [derivedWith, setDerivedWith] = useState<string | null>(null);

  useEffect(() => {
    if (storedRows && storedPoolKey !== null && storedPoolKey !== seededPool) {
      // The metrics behind a price are the derive's and are not stored, so they are carried across
      // rather than lost — otherwise saving would blank the column the operator was reading.
      setRows((previous) => carryModelFacts(storedRows, previous));
      setSeededPool(storedPoolKey);
    }
  }, [storedRows, storedPoolKey, seededPool]);

  // `seededPool` rather than `storedPoolKey` is what says the table has been filled in: between
  // the pool arriving and the effect running, comparing an empty table against three stored rows
  // would flash "unsaved changes" at somebody who has changed nothing.
  const poolDirty = editable && seededPool !== null && poolKey(rows) !== storedPoolKey;
  const poolIssues = useMemo(() => poolProblems(rows), [rows]);

  // One flag for the screen: every way out asks once, about whichever of the two has changes.
  useUnsavedWork(contestDirty || poolDirty);

  // ---------------------------------------------------------------- which model prices it

  // A field of the draft like any other now, so it saves with the rest and takes part in the dirty
  // check. It used to be session storage, because the server had nowhere to record it before
  // publish; §6 opened the write window and that hack went with it.
  const model = draft.salaryModelVersionId;
  const chooseModel = (next: string | null) =>
    setDraft((current) => ({ ...current, salaryModelVersionId: next }));

  const needsSalaries = formatNeedsSalaries(draft.rosterFormat);

  // A format that prices nobody has no pool section, so everything below it moves up one. Derived
  // here because three things read these numbers — the two headings, and the started-events blocker
  // telling an operator which section names the events it is refusing over.
  const lockStep = needsSalaries ? 5 : 4;
  const reviewStep = needsSalaries ? 6 : 5;

  // ---------------------------------------------------------------- what the board scores

  const leaderboards = leaderboardsQ.data ?? [];
  const board = leaderboards.find((l) => l.id === draft.leaderboardId) ?? null;

  /**
   * The scope the server will resolve, worked out the way `LeaderboardRecalculator.ScopeOf` does:
   * the board's stored scope, or its own date range when it has none. Read-only here — a contest
   * does not edit its board's scope, and offering that on this screen would be two places writing
   * one document.
   */
  const scope = useMemo<PointsScope | null>(() => {
    if (!board) return null;
    const stored = readScope(board.scope);
    return {
      ...(stored ?? {
        includeTags: [],
        excludeTags: [],
        includeEventIds: [],
        excludeEventIds: [],
        minFieldSize: 0,
      }),
      // The dates are always the board's own, never the document's — the leaderboard section
      // writes them from those fields for exactly this reason.
      startDate: board.dateStart,
      endDate: board.dateEnd,
    } as PointsScope;
  }, [board]);

  // Debounced because changing the board in section 1 is a click away from changing it again, and
  // this shares the points preview's rate-limit bucket.
  const debouncedScope = useDebouncedValue(scope, 500);
  const scopeQ = useScopeEvents({
    roomId,
    scope: debouncedScope ? toScopeDocument(debouncedScope) : null,
    enabled: debouncedScope !== null,
  });

  // Withheld while the request lags the control: a count about a board the operator has already
  // changed is worse than no count, and here it also drives a publish blocker.
  const scopeSettled =
    debouncedScope !== null &&
    scope !== null &&
    JSON.stringify(toScopeDocument(debouncedScope)) === JSON.stringify(toScopeDocument(scope)) &&
    !scopeQ.isFetching;
  const events = scopeSettled ? (scopeQ.data?.events ?? []) : [];
  const eventsInScope = scopeSettled ? (scopeQ.data?.eventsInScope ?? null) : null;

  /**
   * One clock, read once and passed to both readers of it.
   *
   * Section 5 warns about events that may already have run; section 6 blocks on the ones that
   * certainly have. They ask the same question at different strictness, so a screen where each
   * read its own `new Date()` could — across a midnight, or simply across two renders — warn about
   * an event it was not blocking, or the reverse, with nothing on it able to explain why.
   *
   * <b>UTC, not the browser's date, and that is load-bearing rather than incidental.</b>
   * `certainlyStartedEvents` is only sound in the frame the server compares in, and the server
   * reads a tournament's wall clock as UTC. See that function for the whole argument.
   */
  const todayUtc = new Date().toISOString().slice(0, 10);

  // Null while the resolution is in flight, the same rule `eventsInScope` follows: `events` is
  // empty until it settles, so a count taken from it would read as a confident zero and the
  // blocker would flash off a moment after the screen arrived.
  const startedEventCount = scopeSettled ? certainlyStartedEvents(events, todayUtc).length : null;

  // ---------------------------------------------------------------- lock timing

  // Seeded from the contest, which is null on a draft in practice — `LocksAt` is computed at
  // publish. Read anyway rather than hardcoding empty: a draft that carries one is a fact the box
  // should show, not one for this screen to decide is impossible.
  const [locksAt, setLocksAt] = useState(() => toLocalInput(contest.locksAt));
  const [confirming, setConfirming] = useState<'publish' | 'lock' | 'close' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const transition = CONTEST_TRANSITIONS[status];

  /**
   * Opening a confirmation clears the last attempt's refusal.
   *
   * TanStack keeps `error` until the next `mutate`, so without this a publish refused for a started
   * event would still be sitting in the dialog the next time it opened — describing a problem the
   * operator may have just gone away and fixed. Which is worse than no message, because it reads
   * as the fix not having worked.
   */
  const openConfirmation = (action: 'publish' | 'lock' | 'close') => {
    if (action === 'publish') publish.reset();
    else if (action === 'lock') lock.reset();
    else close.reset();
    setConfirming(action);
  };

  const models = modelsQ.data ?? [];

  // The column stores a version id; `POST /pool/derive` takes a model plus a version number. The
  // catalogue is what resolves one to the other, so a model this room can no longer see resolves
  // to null and the derive is refused with a reason rather than sent with a guess.
  const chosen = models
    .flatMap((m) => m.versions.map((v) => ({ model: m, version: v })))
    .find((pair) => pair.version.id === model);
  const chosenModel = chosen?.model ?? null;

  // §6's publish rule, answered here so it is a reason rather than a round trip. Not folded into
  // `publishBlockers` with the others: that function is about the pool and the scope, and this is
  // about section 3.
  const modelBlocker =
    needsSalaries && model === null
      ? 'Pick a salary model in section 3. A salary cap contest publishes with one or not at all — otherwise there is no record of what priced its pool.'
      : null;

  const blockers = publishBlockers({
    poolSize: rows.length,
    rosterSize: draft.rosterSize ?? 0,
    needsSalaries,
    poolDirty,
    contestDirty,
    eventsInScope,
    startedEventCount,
    lockSection: lockStep,
  });
  const allBlockers = [
    ...contestIssues.map((issue) => issue.message),
    ...poolIssues,
    ...blockers,
    ...(modelBlocker ? [modelBlocker] : []),
  ];

  const unit = budgetUnit({ budgetUnitLabel: draft.budgetUnitLabel || null });
  const mismatch = editable && needsSalaries ? salaryModelMismatch(rows, model, derivedWith) : null;


  const saveContest = () =>
    patch.mutate({
      id: contest.id,
      body: {
        // §6's Draft write window. Sent as a value or as an explicit clear, since null on these
        // contracts means "leave unchanged" — a blank picker has to say so rather than say nothing.
        salaryModelVersionId: draft.salaryModelVersionId,
        clearSalaryModelVersion: draft.salaryModelVersionId === null,
        name: draft.name.trim(),
        // Empty clears, matching the tournament PATCH's convention for its optional strings: null
        // already means "leave unchanged" on these contracts, so a blank box needs its own value.
        description: draft.description.trim(),
        rosterFormat: draft.rosterFormat,
        rosterSize: draft.rosterSize,
        budget: draft.budget,
        budgetUnitLabel: draft.budgetUnitLabel.trim(),
        captainEnabled: draft.captainEnabled,
        // Sent only while captains are on. Clearing it with them off would be a value the CHECK
        // constraint permits and nothing reads, and §5.1's whole warning is about that pair.
        captainMultiplier: draft.captainEnabled ? draft.captainMultiplier : null,
        maxEntriesPerPlayer: draft.maxEntriesPerPlayer,
        leaderboardId: draft.leaderboardId,
      },
    });

  if (!room) return null;

  const actionError =
    describeApiError(patch.error) ??
    describeApiError(savePool.error) ??
    describeApiError(derive.error) ??
    describeApiError(remove.error);

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55 transition hover:text-ink-950"
      >
        ← All contests
      </button>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-semibold text-ink-950">
            {contest.name}
            <Pill tone={CONTEST_STATUS_TONE[status]}>{CONTEST_STATUS_LABEL[status]}</Pill>
          </h2>
          <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-ink-800/60">
            {CONTEST_STATUS_DETAIL[status]}
          </p>
        </div>

        {/* §6's next transition, from every state that has one. Publish is deliberately *not* here
            — it belongs at the foot of section 6, after the review it is the conclusion of. */}
        {transition && transition.action !== 'publish' ? (
          <button
            type="button"
            onClick={() => openConfirmation(transition.action)}
            disabled={lock.isPending || close.isPending}
            className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:opacity-50"
          >
            {transition.label}
          </button>
        ) : null}
      </div>

      {/* §6.2's Hold. Above the sections because it is about what the contest is doing right
          now rather than about how it was set up — and because a screen that showed a frozen board
          with nothing said is the thing §6.2 calls worse than the drift itself. Signal orange: it
          is a job somebody has to do, and only an administrator can do it. */}
      {contest.standingsPaused ? (
        <div
          role="status"
          className="mb-4 border border-line border-l-4 border-l-warn bg-surface p-4 text-[12.5px] leading-relaxed text-ink-900"
        >
          <p>
            <strong className="text-ink-950">Standings paused.</strong>{' '}
            {pausedNotice(contest.standingsPausedAt)}
          </p>
          <p className="mt-1.5 text-ink-800/60">
            {/* Named rather than linked: the drift report is admin-only, so an operator's screen
                firing that request would meet a 403 and read as a broken app. */}
            A platform administrator can see which events changed and decide what to do about it.
            Until then nothing about this contest moves.
          </p>
        </div>
      ) : null}

      {/* §6.2 and §7.2's amendment marker. Stated for everybody, not only administrators: an
          operator asked to defend a set of standings is being asked about a board that changed
          after entrants committed to it, and the answer has to be on the screen.

          Deliberately not orange and deliberately not a warning. Adopting is a decision somebody
          made on purpose with a reason recorded on the snapshot; the honest case and a mistake
          produce identical rows, so a colour that said "look at this" would be wrong about half of
          them. Same call as the memberships panel's contact-change note. */}
      {contest.amendedAfterLock ? (
        <div
          role="note"
          className="mb-4 border border-line bg-surface p-4 text-[12.5px] leading-relaxed text-ink-800/75"
        >
          <strong className="text-ink-950">Amended after lock.</strong>{' '}
          {AMENDED_AFTER_LOCK_NOTICE}
        </div>
      ) : null}

      {/* §6.2's decision, administrator-only. The panel renders nothing at all when there is no
          drift, so a healthy contest does not open under an empty box. */}
      <ContestDriftPanel contest={contest} roomId={roomId} isAdmin={isAdmin} />

      <ErrorNotice message={actionError} className="mb-4" />

      <div className="space-y-6 border border-line bg-surface p-5">
        <SourceSection
          step={1}
          draft={draft}
          onChange={setDraft}
          issues={contestIssues}
          leaderboards={leaderboards}
          eventsInScope={eventsInScope}
          scopeFailure={scopeQ.isError ? describeApiError(scopeQ.error) : null}
          onRetryScope={() => void scopeQ.refetch()}
          readOnly={!editable}
        />

        <FormatSection
          step={2}
          draft={draft}
          onChange={setDraft}
          issues={contestIssues}
          unit={unit}
          readOnly={!editable}
        />

        {needsSalaries ? (
          <ContestSalarySection
            step={3}
            models={models}
            modelsLoading={modelsQ.isLoading}
            choice={model}
            onChoiceChange={chooseModel}
            pinnedVersionId={contest.salaryModelVersionId}
            readOnly={!editable}
            mismatch={mismatch}
          />
        ) : (
          <BuilderSection step={3} title="Salary model">
            <p className="text-[12px] leading-relaxed text-ink-800/60">
              {/* The assumption §3's extension table exists to keep out of the code, said out loud:
                  an auction discovers its prices by bidding, so there is nothing to pick here. */}
              This format works out its own prices, so there is no salary model to choose and no
              pool to review.
            </p>
          </BuilderSection>
        )}

        {needsSalaries ? (
          poolQ.isLoading ? (
            <BuilderSection step={4} title="Pool review">
              <Spinner label="Loading the pool…" />
            </BuilderSection>
          ) : (
            <ContestPoolReview
              step={4}
              contestId={contest.id}
              contestName={contest.name}
              contestStatus={status}
              rows={rows}
              onRowsChange={setRows}
              unit={unit}
              rosterSize={draft.rosterSize ?? 0}
              frozen={!editable}
              usingModel={model !== null}
              lastDerive={lastDerive}
              deriving={derive.isPending}
              deriveDisabledReason={
                model === null
                  ? 'No salary model is chosen in section 3, so there is nothing to run. Add players below by hand, or pick a model.'
                  : chosen === undefined
                    ? 'The salary model this contest names is not in this room’s catalogue, so it cannot be run. Pick one in section 3.'
                    : null
              }
              onDerive={() => {
                if (!chosen) return;
                derive.mutate(
                  {
                    id: contest.id,
                    salaryModelId: chosen!.model.id,
                    version: chosen!.version.versionNumber,
                    leaderboardIds: null,
                  },
                  {
                    onSuccess: (result) => {
                      setLastDerive(result);
                      setDerivedWith(model);
                      setRows((previous) => mergeDerived(previous, result.entries));
                    },
                  },
                );
              }}
              roomId={roomId}
            />
          )
        ) : null}

        <ContestLockSection
          step={lockStep}
          locksAt={locksAt}
          onLocksAtChange={setLocksAt}
          events={events}
          eventsKnown={scopeSettled}
          storedLocksAt={contest.locksAt}
          readOnly={!editable}
          todayUtc={todayUtc}
        />

        {editable ? (
          <ContestReviewSection
            step={reviewStep}
            draft={draft}
            leaderboard={board}
            eventsInScope={eventsInScope}
            poolSize={rows.length}
            needsSalaries={needsSalaries}
            model={chosenModel}
            modelVersion={chosen?.version.versionNumber ?? null}
            locksAt={locksAt}
            blockers={allBlockers}
            onPublish={() => openConfirmation('publish')}
            publishing={publish.isPending}
          />
        ) : null}
      </div>

      {editable ? (
        <Footer
          contestDirty={contestDirty}
          poolDirty={poolDirty}
          canSaveContest={contestIssues.length === 0}
          canSavePool={poolIssues.length === 0}
          savingContest={patch.isPending}
          savingPool={savePool.isPending}
          onSaveContest={saveContest}
          onSavePool={() =>
            savePool.mutate({ id: contest.id, players: poolRequestOf(rows) })
          }
          entries={contest.entries}
          deleting={remove.isPending}
          confirmDelete={confirmDelete}
          onConfirmDelete={setConfirmDelete}
          onDelete={() => remove.mutate(contest.id, { onSuccess: onDeleted })}
        />
      ) : null}

      <ContestEntriesPanel contest={contest} />

      {confirming && transition && transition.action === confirming ? (
        <ContestTransitionDialog
          contestName={contest.name}
          transition={transition}
          pending={publish.isPending || lock.isPending || close.isPending}
          error={
            confirming === 'publish'
              ? publish.error
              : confirming === 'lock'
                ? lock.error
                : close.error
          }
          extra={
            confirming === 'publish' ? (
              <p className="border border-line bg-ground/40 p-3 text-[11.5px] leading-relaxed text-ink-800/70">
                {/* Stated because this is the moment the choice stops being editable. The draft
                    has carried it since section 3, but "frozen" is a claim worth making where it
                    becomes true rather than where it was made. */}
                {needsSalaries
                  ? chosen
                    ? `Freezes ${chosen.model.name} v${chosen.version.versionNumber} as the model behind these prices, and ${rows.length} ${rows.length === 1 ? 'player' : 'players'} at the prices below.`
                    : `Records this pool as priced by hand — no salary model — and freezes ${rows.length} ${rows.length === 1 ? 'player' : 'players'}.`
                  : 'This format prices itself, so nothing is pinned.'}
                {mismatch ? <span className="mt-1.5 block text-warn">{mismatch}</span> : null}
              </p>
            ) : null
          }
          onClose={() => setConfirming(null)}
          onConfirm={() => {
            const done = { onSuccess: () => setConfirming(null) };
            if (confirming === 'publish') {
              publish.mutate(
                {
                  id: contest.id,
                  body: {
                    // Deliberately null. §6 makes publish *freeze* the model rather than write it,
                    // and the draft is saved before this is reachable — naming one here would be a
                    // second source of truth for a decision section 3 already recorded.
                    salaryModelId: null,
                    version: null,
                    // Empty means §6.1's default — the start of the earliest event in scope, which
                    // the server has just resolved and this screen only approximates.
                    locksAt: fromLocalInput(locksAt),
                    opensAt: null,
                  },
                },
                done,
              );
            } else if (confirming === 'lock') {
              lock.mutate(contest.id, done);
            } else {
              close.mutate(contest.id, done);
            }
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 1. source leaderboard

/**
 * Section 1 — which leaderboard this scores from, which is the decision the whole contest hangs
 * off (locked rule 1: a contest attaches to a leaderboard and inherits its tournament list).
 *
 * <b>The event count comes from the server and is blank while it is stale.</b> Printing the last
 * count beside a board the operator has just changed states a number about something else, and
 * this count also drives a publish blocker — a wrong one would send somebody off to fix a scope
 * that was working.
 */
function SourceSection({
  step,
  draft,
  onChange,
  issues,
  leaderboards,
  eventsInScope,
  scopeFailure,
  onRetryScope,
  readOnly,
}: {
  step: number;
  draft: ContestDraft;
  onChange: (next: ContestDraft) => void;
  issues: readonly ContestIssue[];
  leaderboards: readonly { id: string; name: string; dateStart: string; dateEnd: string; status: string }[];
  eventsInScope: number | null;
  /** Why the board's event count failed, worded by the server; null while it is counting or has counted. */
  scopeFailure: string | null;
  onRetryScope: () => void;
  readOnly: boolean;
}) {
  const board = leaderboards.find((l) => l.id === draft.leaderboardId) ?? null;

  return (
    <BuilderSection
      step={step}
      title="Source leaderboard"
      summary={board ? `${board.name} · ${board.dateStart} to ${board.dateEnd}` : 'Not set.'}
    >
      <div className="space-y-3">
        <Labelled label="Name" hint="What entrants see.">
          <input
            type="text"
            value={draft.name}
            disabled={readOnly}
            maxLength={120}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            className={inputClass}
          />
        </Labelled>

        <Labelled
          label="Description"
          hint="Optional. One line telling entrants what this is — shown beside the contest in the portal."
        >
          <input
            type="text"
            value={draft.description}
            disabled={readOnly}
            maxLength={500}
            onChange={(e) => onChange({ ...draft, description: e.target.value })}
            placeholder="Pick eight players from the summer series"
            className={inputClass}
          />
        </Labelled>

        <Labelled
          label="Scores from"
          hint="The contest takes its events and its scoring from this leaderboard. Whatever a player scores there is what they score for the teams that drafted them."
        >
          <select
            value={draft.leaderboardId}
            disabled={readOnly}
            onChange={(e) => onChange({ ...draft, leaderboardId: e.target.value })}
            className={`${controlClass} w-full`}
          >
            {board === null ? <option value={draft.leaderboardId}>Not in this room’s list</option> : null}
            {leaderboards.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} · {l.dateStart} to {l.dateEnd}
              </option>
            ))}
          </select>
        </Labelled>

        <IssueList issues={contestIssuesFor(issues, 'source')} />

        {board ? (
          <Worked>
            {eventsInScope === null ? (
              scopeFailure ? (
                // The server's own sentence, and a way to ask again: the count never retries and is cached per
                // scope, so a rate-limit refusal would otherwise outlast its own minute (#222).
                <>
                  {scopeFailure}{' '}
                  <button type="button" onClick={onRetryScope} className="underline underline-offset-2">
                    Try again
                  </button>
                </>
              ) : (
                'Counting the events this board scores…'
              )
            ) : (
              `${board.name} scores ${eventsInScope} ${eventsInScope === 1 ? 'event' : 'events'}. Change what counts on the leaderboard itself — a contest reads that list rather than keeping one of its own.`
            )}
          </Worked>
        ) : null}

        {readOnly ? (
          <p className="text-[11.5px] leading-relaxed text-ink-800/60">
            Fixed when this contest published. Entrants drafted knowing which series they were
            following, so it is not something to move afterwards.
          </p>
        ) : null}
      </div>
    </BuilderSection>
  );
}

// ---------------------------------------------------------------- 2. format and budget

/**
 * Section 2 — the rules of the game.
 *
 * <b>Every worked example here is computed from the operator's own figures</b>, §9.5's habit: a
 * budget of 200 over a roster of 8 is an average of 25 a player, which is the sentence that makes a
 * budget judgeable. Prose about a hypothetical contest would not.
 */
function FormatSection({
  step,
  draft,
  onChange,
  issues,
  unit,
  readOnly,
}: {
  step: number;
  draft: ContestDraft;
  onChange: (next: ContestDraft) => void;
  issues: readonly ContestIssue[];
  unit: string;
  readOnly: boolean;
}) {
  const average =
    draft.budget !== null && draft.rosterSize !== null && draft.rosterSize > 0
      ? draft.budget / draft.rosterSize
      : null;

  return (
    <BuilderSection
      step={step}
      title="Format and budget"
      summary={`${draft.rosterSize ?? '?'} players under ${draft.budget ?? '?'} ${unit}${
        draft.captainEnabled ? `, captain at ×${draft.captainMultiplier ?? '?'}` : ''
      }`}
    >
      <div className="space-y-4">
        <Labelled label="How entrants build a team">
          {/* `SegmentedControl` disables per option rather than as a whole, so read-only is
              expressed by disabling all of them — which also keeps the reserved formats reading as
              reserved rather than as merely unavailable right now. */}
          <SegmentedControl
            ariaLabel="Roster format"
            options={ROSTER_FORMATS.map((format) => ({
              ...format,
              disabled: readOnly || format.disabled,
              title: readOnly ? 'Fixed when this contest published.' : format.title,
            }))}
            value={draft.rosterFormat}
            onChange={(next: RosterFormat) => onChange({ ...draft, rosterFormat: next })}
          />
        </Labelled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labelled label="Players per team">
            <NumberInput
              value={draft.rosterSize}
              min={1}
              disabled={readOnly}
              ariaLabel="Players per team"
              className="w-24"
              onChange={(next) => onChange({ ...draft, rosterSize: next })}
            />
          </Labelled>

          <Labelled label="Budget">
            <div className="flex flex-wrap items-center gap-2">
              <NumberInput
                value={draft.budget}
                min={0}
                disabled={readOnly}
                ariaLabel="Budget"
                className="w-28"
                onChange={(next) => onChange({ ...draft, budget: next })}
              />
              <input
                type="text"
                value={draft.budgetUnitLabel}
                disabled={readOnly}
                maxLength={20}
                placeholder="units"
                aria-label="What the budget is measured in"
                onChange={(e) => onChange({ ...draft, budgetUnitLabel: e.target.value })}
                className={`${controlClass} w-24`}
              />
            </div>
          </Labelled>
        </div>

        {average !== null ? (
          <Worked>
            {/* The number that decides whether a pool is draftable at all: if most of the pool
                costs more than this, every roster is the same eight cheap players. */}
            {Number(average.toFixed(2)).toLocaleString('en-US')} {unit} a player on average. Prices
            in section 4 want to spread either side of that — a pool where most players cost more
            than the average leaves entrants no real choices.
          </Worked>
        ) : null}

        <div className="space-y-2">
          <CheckboxRow
            checked={draft.captainEnabled}
            disabled={readOnly}
            label="One player can be captain"
            detail="Their points are multiplied. It is the one decision that separates two entrants who picked the same eight players."
            onChange={(on) =>
              onChange({
                ...draft,
                captainEnabled: on,
                // Ticked with no multiplier is the exact state §5.1's CHECK constraint exists to
                // refuse, so the default arrives with the box rather than being left blank for the
                // operator to trip over.
                captainMultiplier: on ? (draft.captainMultiplier ?? 1.5) : null,
              })
            }
          />
          {draft.captainEnabled ? (
            <Labelled label="Captain multiplier" className="pl-6">
              <NumberInput
                value={draft.captainMultiplier}
                min={0}
                step={0.1}
                disabled={readOnly}
                ariaLabel="Captain multiplier"
                className="w-24"
                invalid={draft.captainMultiplier !== null && draft.captainMultiplier <= 0}
                onChange={(next) => onChange({ ...draft, captainMultiplier: next })}
              />
            </Labelled>
          ) : null}
        </div>

        <IssueList issues={contestIssuesFor(issues, 'format')} />

        <p className="border-t border-line pt-3 text-[11px] leading-relaxed text-ink-800/50">
          {/* Two facts stated rather than shown as controls that do nothing. §12 defers paid entry
              (the fee is constrained to zero in the schema) and multi-entry (the columns exist, the
              screens and the standings do not). */}
          Entry is free — paid contests are not something this product does yet. One team per
          person.
        </p>
      </div>
    </BuilderSection>
  );
}

// ---------------------------------------------------------------- the footer

/**
 * Two saves, side by side, because there are two documents.
 *
 * The dirty label is signal orange for the reason the salary builder's is: unsaved work is
 * literally "you must act", which is what that colour means in this app. Delete sits at the far
 * left of the destructive end and asks twice — a contest with entries is refused by the server
 * anyway (409 `contest_has_entries`), and this says so before the attempt.
 */
function Footer({
  contestDirty,
  poolDirty,
  canSaveContest,
  canSavePool,
  savingContest,
  savingPool,
  onSaveContest,
  onSavePool,
  entries,
  deleting,
  confirmDelete,
  onConfirmDelete,
  onDelete,
}: {
  contestDirty: boolean;
  poolDirty: boolean;
  canSaveContest: boolean;
  canSavePool: boolean;
  savingContest: boolean;
  savingPool: boolean;
  onSaveContest: () => void;
  onSavePool: () => void;
  entries: number;
  deleting: boolean;
  confirmDelete: boolean;
  onConfirmDelete: (next: boolean) => void;
  onDelete: () => void;
}) {
  const dirty = contestDirty || poolDirty;

  return (
    <div className="sticky bottom-0 z-10 -mx-8 mt-6 flex flex-wrap items-center gap-3 border-t border-line bg-ground/95 px-8 py-4 backdrop-blur">
      <span className="mr-auto text-xs">
        {dirty ? (
          <>
            <span className="font-semibold uppercase tracking-[0.06em] text-signal">
              Unsaved changes
            </span>
            <span className="ml-1.5 text-ink-800/45">
              {contestDirty && poolDirty
                ? 'The contest and the pool.'
                : contestDirty
                  ? 'The contest’s settings.'
                  : 'The pool.'}
            </span>
          </>
        ) : (
          <span className="text-ink-800/45">All changes saved</span>
        )}
      </span>

      {confirmDelete ? (
        <>
          <span className="text-xs text-ink-800/70">
            {entries > 0
              ? 'This has entries — the server will refuse.'
              : 'Delete this contest for good?'}
          </span>
          <button
            type="button"
            onClick={() => onConfirmDelete(false)}
            className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="border border-chip-500/40 px-4 py-2 text-sm font-medium text-chip-500 transition hover:bg-chip-500/5 disabled:opacity-40"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => onConfirmDelete(true)}
          className="border border-chip-500/40 px-4 py-2 text-sm text-chip-500 transition hover:bg-chip-500/5"
        >
          Delete draft
        </button>
      )}

      <button
        type="button"
        onClick={onSavePool}
        disabled={!poolDirty || !canSavePool || savingPool}
        title={poolDirty && !canSavePool ? 'Fix the prices in section 4 first.' : undefined}
        className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {savingPool ? 'Saving…' : 'Save pool'}
      </button>
      <button
        type="button"
        onClick={onSaveContest}
        disabled={!contestDirty || !canSaveContest || savingContest}
        className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {savingContest ? 'Saving…' : 'Save contest'}
      </button>
    </div>
  );
}
